/**
 * Compact KPI settings: gear beside Table/Cards. Tenant RYG bands live in the popup.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import api from '@/services/api';
import { useLanguage } from '@/state/language/LanguageProvider';
import toast from 'react-hot-toast';
import { KpiThresholdFields, parseThresholdDraft, type ThresholdDraft } from './KpiThresholdFields';

const DEFAULT_DRAFT: ThresholdDraft = { yellowOffPct: '0', redOffPct: '20' };

export const KpiTenantThresholds: React.FC = () => {
  const { t } = useLanguage();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ThresholdDraft>(DEFAULT_DRAFT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get('/kpis/thresholds')
      .then((response) => {
        const data = response.data?.data;
        if (!data) return;
        setDraft({
          yellowOffPct: String(data.yellowOffPct ?? 0),
          redOffPct: String(data.redOffPct ?? 20)
        });
      })
      .catch(() => undefined);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const save = async () => {
    const parsed = parseThresholdDraft(draft);
    if (!parsed) {
      toast.error(
        t('kpi.thresholdInvalid') ||
          'Yellow and red must both be 0–100, and yellow cannot be larger than red.'
      );
      return;
    }
    try {
      setSaving(true);
      await api.put('/kpis/thresholds', parsed);
      toast.success(t('kpi.thresholdSaved') || 'Default bands saved.');
      setOpen(false);
    } catch {
      toast.error(t('kpi.thresholdSaveFailed') || 'Could not save default bands.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <Button
        type="button"
        variant={open ? 'primary' : 'ghost'}
        size="sm"
        className="flex items-center"
        title={t('kpi.thresholdSettings') || 'KPI settings'}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
      >
        <Cog6ToothIcon className="w-4 h-4" aria-hidden />
        <span className="sr-only">{t('kpi.thresholdSettings') || 'KPI settings'}</span>
      </Button>
      {open && (
        <div
          role="dialog"
          aria-label={t('kpi.thresholdSettings') || 'KPI settings'}
          className="absolute right-0 top-full z-30 mt-1 w-64 p-2.5 border border-[var(--color-border)] rounded bg-[var(--color-surface-bg)] shadow-md"
        >
          <h2 className="text-xs font-medium text-[var(--color-surface-text)] mb-0.5">
            {t('kpi.thresholdTenantTitle') || 'Default status bands'}
          </h2>
          <p className="text-[10px] text-[var(--color-secondary-text)] mb-1.5 leading-tight">
            {t('kpi.thresholdTenantHint') ||
              'Yellow and red are % off the selected plan target. A KPI uses these unless it has its own pair. Composites always use these defaults.'}
          </p>
          <KpiThresholdFields draft={draft} onChange={setDraft} disabled={saving} t={t} />
          <div className="flex justify-end mt-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                void save();
              }}
              className="text-[11px] px-2 py-0.5 rounded bg-[var(--color-primary-bg)] text-[var(--color-secondary-text)] disabled:opacity-50"
            >
              {t('kpi.thresholdSave') || 'Save defaults'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
